import { has, set, omit, keyBy, sortBy, map, reject } from 'lodash/fp';
import createReducer from '../../../../../helpers/createReducer';
import {
  fetchInventoryPackageItems,
  fetchInventoryItemOptions,
  fetchInventoryTagOptions,
  addInventoryPackageItem as onAddedInventoryPackageItem,
  updateInventoryPackageItem as onUpdatedInventoryPackageItem,
  deleteInventoryPackageItem as onDeletedInventoryPackageItem,
  fetchInventoryPackageActiveReservationOrderItemsCount
} from '../../../sagas/routines';
import {
  resetFormPackageItems,
  addInventoryPackageItem,
  deleteInventoryPackageItem,
  undoDeleteInventoryPackageItem,
  incrementQtyInventoryPackageItem,
  decrementQtyInventoryPackageItem,
  clearPackageItems,
  clearPackageActiveReservationOrderItemsCount,
  addItemOption,
  updateItemOption,
  removeItemOption
} from '../../../actions';

const initialState = {
  packageItems: {},
  formPackageItems: {},
  packageActiveReservationOrderItemsCount: 0,
  fetchingPackageItems: false,
  fetchedPackageItems: false,
  itemOptions: [],
  fetchingItemOptions: false,
  fetchedItemOptions: false,
  tagOptions: [],
  fetchingTagOptions: false,
  fetchedTagOptions: false,
  fetchingPackageActiveReservationOrderItemsCount: false,
  fetchedPackageActiveReservationOrderItemsCount: false
};

const fetchItemOptionsHandler = routine => ({
  [routine.REQUEST]: state => ({
    ...state,
    fetchingItemOptions: true,
    fetchedItemOptions: false
  }),
  [routine.SUCCESS]: (state, { payload: { response } }) =>
    set('itemOptions', response.items, state),
  [routine.FULFILL]: state => ({
    ...state,
    fetchingItemOptions: false,
    fetchedItemOptions: true
  })
});

const fetchTagOptionsHandler = routine => ({
  [routine.REQUEST]: state => ({
    ...state,
    fetchingTagOptions: true,
    fetchedTagOptions: false
  }),
  [routine.SUCCESS]: (state, { payload: { response } }) =>
    set('tagOptions', response.items, state),
  [routine.FULFILL]: state => ({
    ...state,
    fetchingTagOptions: false,
    fetchedTagOptions: true
  })
});

const fetchPackageItemsHandler = routine => ({
  [routine.REQUEST]: state => ({
    ...state,
    fetchingPackageItems: true,
    fetchedPackageItems: false
  }),
  [routine.SUCCESS]: (state, { payload: { response } }) => ({
    ...state,
    packageItems: keyBy('id', response.items),
    formPackageItems: keyBy('id', response.items)
  }),
  [routine.FULFILL]: state => ({
    ...state,
    fetchingPackageItems: false,
    fetchedPackageItems: true
  })
});

const fetchActiveReservationOrderItemsCountHandler = routine => ({
  [routine.REQUEST]: state => ({
    ...state,
    fetchingPackageActiveReservationOrderItemsCount: true,
    fetchedPackageActiveReservationOrderItemsCount: false
  }),
  [routine.SUCCESS]: (state, { payload: { response } }) => ({
    ...state,
    packageActiveReservationOrderItemsCount: response.count
  }),
  [routine.FULFILL]: state => ({
    ...state,
    fetchingPackageActiveReservationOrderItemsCount: false,
    fetchedPackageActiveReservationOrderItemsCount: true
  })
});

const resetFormPackageItemsHandler = action => ({
  [action.toString()]: state =>
    set('formPackageItems', state.packageItems, state)
});

const composeItemOption = ({
  id: value,
  name: label,
  price,
  weight,
  points
}) => ({
  value,
  label,
  meta: { price, weight, points }
});

const addItemOptionHandler = action => ({
  [action.toString()]: (state, { payload: item }) => ({
    ...state,
    itemOptions: sortBy('label', [
      ...state.itemOptions,
      composeItemOption(item)
    ])
  })
});

const updateItemOptionHandler = action => ({
  [action.toString()]: (state, { payload: item }) => ({
    ...state,
    itemOptions: map(
      option => (option.value === item.id ? composeItemOption(item) : option),
      state.itemOptions
    )
  })
});

const removeItemOptionHandler = action => ({
  [action.toString()]: (state, { payload: value }) => ({
    ...state,
    itemOptions: reject({ value }, state.itemOptions)
  })
});

const addInventoryPackageItemHandler = action => ({
  [action.toString()]: (state, { payload: packageItem }) => ({
    ...state,
    formPackageItems: {
      ...state.formPackageItems,
      [packageItem.id]: {
        ...packageItem,
        added: true
      }
    }
  })
});

const incrementQtyInventoryPackageItemHandler = action => ({
  [action.toString()]: (state, { payload: item }) => {
    const isExisting = has(item.id, state.packageItems);
    const quantity = item.quantity + 1;
    const editedItem = isExisting
      ? { ...item, quantity, edited: true }
      : { ...item, quantity };
    return {
      ...state,
      formPackageItems: set(item.id, editedItem, state.formPackageItems)
    };
  }
});

const decrementQtyInventoryPackageItemHandler = action => ({
  [action.toString()]: (state, { payload: item }) => {
    const isExisting = has(item.id, state.packageItems);
    const quantity = item.quantity - 1;
    const editedItem = isExisting
      ? { ...item, quantity, edited: true }
      : { ...item, quantity };
    return {
      ...state,
      formPackageItems: set(item.id, editedItem, state.formPackageItems)
    };
  }
});

const deleteInventoryPackageItemHandler = action => ({
  [action.toString()]: (state, { payload: item }) => {
    const isExisting = has(item.id, state.packageItems);
    return {
      ...state,
      formPackageItems: isExisting
        ? set(`${item.id}.deleted`, true, state.formPackageItems)
        : omit(item.id, state.formPackageItems)
    };
  }
});
const undoDeleteInventoryPackageItemHandler = action => ({
  [action.toString()]: (state, { payload: item }) => ({
    ...state,
    formPackageItems: set(
      item.id,
      omit('deleted', item),
      state.formPackageItems
    )
  })
});

const clearPackageItemsHandler = action => ({
  [action.toString()]: set('packageItems', {})
});

const clearPackageActiveReservationOrderItemsCountHandler = action => ({
  [action.toString()]: set('packageActiveReservationOrderItemsCount', 0)
});

const onAddedInventoryPackageItemHandler = routine => ({
  [routine.SUCCESS]: (state, { payload: { response: packageItem } }) => ({
    ...state,
    packageItems: set(packageItem.id, packageItem, state.packageItems),
    formPackageItems: {
      ...omit(`new_${packageItem.item.id}`, state.formPackageItems),
      [packageItem.id]: packageItem
    }
  })
});

const onUpdatedInventoryPackageItemHandler = routine => ({
  [routine.SUCCESS]: (state, { payload: { response: packageItem } }) => ({
    ...state,
    packageItems: set(packageItem.id, packageItem, state.packageItems),
    formPackageItems: set(packageItem.id, packageItem, state.formPackageItems)
  })
});

const onDeletedInventoryPackageItemHandler = routine => ({
  [routine.SUCCESS]: (state, { payload: { packageItemId } }) => ({
    ...state,
    packageItems: omit(packageItemId, state.packageItems),
    formPackageItems: omit(packageItemId, state.formPackageItems)
  })
});

export default createReducer(initialState, {
  ...fetchTagOptionsHandler(fetchInventoryTagOptions),
  ...fetchItemOptionsHandler(fetchInventoryItemOptions),
  ...fetchPackageItemsHandler(fetchInventoryPackageItems),
  ...fetchActiveReservationOrderItemsCountHandler(
    fetchInventoryPackageActiveReservationOrderItemsCount
  ),
  ...resetFormPackageItemsHandler(resetFormPackageItems),
  ...addInventoryPackageItemHandler(addInventoryPackageItem),
  ...incrementQtyInventoryPackageItemHandler(incrementQtyInventoryPackageItem),
  ...decrementQtyInventoryPackageItemHandler(decrementQtyInventoryPackageItem),
  ...deleteInventoryPackageItemHandler(deleteInventoryPackageItem),
  ...undoDeleteInventoryPackageItemHandler(undoDeleteInventoryPackageItem),
  ...onAddedInventoryPackageItemHandler(onAddedInventoryPackageItem),
  ...onUpdatedInventoryPackageItemHandler(onUpdatedInventoryPackageItem),
  ...onDeletedInventoryPackageItemHandler(onDeletedInventoryPackageItem),
  ...clearPackageItemsHandler(clearPackageItems),
  ...clearPackageActiveReservationOrderItemsCountHandler(
    clearPackageActiveReservationOrderItemsCount
  ),
  ...addItemOptionHandler(addItemOption),
  ...updateItemOptionHandler(updateItemOption),
  ...removeItemOptionHandler(removeItemOption)
});
